import React, { Fragment, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Image from 'next/image';
import { Row, Col, Text, Alert } from '../../designSystem';
import AvailablePlanCard from '../AvailablePlanCard';
import PlansRetry from '../PlansRetry';

import styles from './PlansStep.module.scss';

import {
  AvailablePlans as AvailablePlansType,
  AvailablePlan as AvailablePlanType,
} from '../../types/availablePlans';
import { UF as UFType } from '../../types/uf';
import componentsId from '../../utils/componentsId';
import { plansStepTexts } from '../../utils/generalTexts';
import { formatMoney } from '../../utils/format';

import * as appSelectors from '../../redux/app/selectors';
import * as ufSelectors from '../../redux/uf/selectors';
import * as plansSelectors from '../../redux/plans/selectors';
import * as wizardSelectors from '../../redux/wizard/selectors';
import * as wizardActions from '../../redux/wizard/actions';
import { bicevidaURLWindowIndividualInsurance } from '../../utils/constants';
import {
  clickActionsEvents,
  quotationFlowEvents,
  trackPageViewQuotation,
} from '@/utils/dataLayer/quotationFlow';
import { CAT } from '@/utils/assets';
import IncrementDecrement from '../IncrementDecrement';
import WizardStepper from '../Wizard/WizardStepper';

type WorkersT = {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
};

function PlansStep() {
  const dispatch = useDispatch();

  const error = useSelector(appSelectors.getError);
  const UF: UFType = useSelector(ufSelectors.getUF);
  const availablePlans: AvailablePlansType = useSelector(plansSelectors.getAvailablePlans);
  const branding = useSelector(wizardSelectors.getBranding);
  const planSelected: string = useSelector(wizardSelectors.getPrePlanSelectedRestored);
  const valueUF = formatMoney(UF.value, { round: false, decimals: 2 });
  const getDataLayerInfo = useSelector(wizardSelectors.getDataLayerInfo);
  const workersSelected = useSelector(wizardSelectors.getWorkersSelected);

  const today = UF.date && UF.date.split('-').reverse().join('/');

  const setPlanSelected = (id: string, type: string) => {
    if (planSelected !== id) {
      dispatch(wizardActions.setPrePlanSelected(id));
      dispatch(wizardActions.setPlanSelected(id));
      dispatch(wizardActions.setPrePlanType(type));
      dispatch(wizardActions.setToaster(plansStepTexts.toaster));
    }
  };

  useEffect(() => {
    if (!!planSelected) {
      const btnNext = document.getElementById(componentsId.plansStep.btnNext);
      btnNext?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [planSelected]);

  const availablePlansFiltered = useMemo(
    () =>
      availablePlans.filter(
        (availablePlan) => availablePlan.base !== null && availablePlan.hidePlan !== true
      ),
    [availablePlans]
  );

  useEffect(() => {
    const plan = availablePlans.find((plan: AvailablePlanType) => plan._id === planSelected);

    if (plan) {
      dispatch(wizardActions.setPrePlanSelected(plan._id));
      dispatch(wizardActions.setPrePlanType(plan.type));
    }
  }, [planSelected]);

  useEffect(() => {
    trackPageViewQuotation({
      eventParams: {
        ...quotationFlowEvents.planStep,
        ...getDataLayerInfo.event_params,
        attribution: branding.code,
      },
      user: getDataLayerInfo.user,
    });
  }, []);

  const setWorkersSelected = (workers: number) => dispatch(wizardActions.setWorkers(workers));

  const handleWorkersChange = async (workers: number, action?: 'increment' | 'decrement') => {
    const actionMap = {
      decrement: clickActionsEvents.decrementWorkers,
      increment: clickActionsEvents.incrementWorkers,
    };

    if (action && actionMap[action]) {
      trackPageViewQuotation({
        eventParams: {
          ...quotationFlowEvents.planStep,
          ...getDataLayerInfo.event_params,
          action_type: 'click',
          action: actionMap[action],
        },
        user: getDataLayerInfo.user,
      });
    }

    setWorkersSelected(workers);
  };

  return error ? (
    <Fragment>
      <Row justify="center" className={styles.plans_step_container}>
        <Col sm={12} md={12} lg={9} xl={8} className={styles.step_plans_retry_container}>
          <PlansRetry />
        </Col>
      </Row>
    </Fragment>
  ) : (
    <div className={styles.plans_step_container}>
      <Image src={CAT.CAT_GREETING_CIRCLE_VARIABLE} alt="saludando" width={93} height={80} />
      <Row justify="center" className={styles.plan_row_title}>
        <Col>
          <Text text={plansStepTexts.title} customClass={styles.plan_title} />
        </Col>
      </Row>
      <WizardStepper />

      <div>
        <div className={styles.plans_row_container}>
          <div className={styles.plans_row}>
            <div className={styles.plan_description}>
              <div className={styles.plan_cat}>
                <Image
                  src={CAT.THINKING_TRANSPARENT}
                  alt="gato bicente pensado"
                  height={46}
                  width={46}
                />
              </div>
              <div className={styles.plan_description_text}>
                <Text text="¿Cuántos trabajadores quieres proteger?" />
                <Text text="Para cotizar debes agregar mínimo 5 trabajadores." />
              </div>
            </div>
            <IncrementDecrement
              quantity={workersSelected}
              handleChange={handleWorkersChange}
              hasError={!workersSelected || workersSelected < 5}
            />
          </div>
          {(!workersSelected || workersSelected < 5) && (
            <Alert
              text={
                <>
                  Si tienes menos de 5 trabajadores, te recomendamos contratar un{' '}
                  <a target="_blank" href={bicevidaURLWindowIndividualInsurance} rel="noreferrer">
                    Seguro Complementario de Salud Individual
                  </a>
                </>
              }
              customClass={styles.plans_workers_alert}
              iconName="warning"
            />
          )}
        </div>

        <div className={styles.plan_steps_card_row}>
          {availablePlansFiltered.map((availablePlan: AvailablePlanType, idx: number) => (
            <AvailablePlanCard
              key={`col_available_plan_card_${idx}`}
              id={componentsId.plansStep.cardId}
              idx={idx}
              availablePlan={availablePlan}
              UF={UF}
              customClass={styles.plan_steps_card}
              pagePrefix={componentsId.plansStep.cards}
              selectable
              allDisabled={!workersSelected || workersSelected < 5}
              btnAction={{
                id: `${componentsId.plansStep.cardsBtn}${idx}`,
                text: plansStepTexts.btnCoverage,
                onClickEvent: () => {
                  trackPageViewQuotation({
                    eventParams: {
                      ...quotationFlowEvents.planStep,
                      ...getDataLayerInfo.event_params,
                      action_type: 'click',
                      action: clickActionsEvents.planStepModalPlan(availablePlan.description),
                    },
                    user: getDataLayerInfo.user,
                  });
                },
              }}
              planSelected={planSelected}
              setPlanSelected={() => {
                trackPageViewQuotation({
                  eventParams: {
                    ...quotationFlowEvents.planStep,
                    ...getDataLayerInfo.event_params,
                    action_type: 'click',
                    action: clickActionsEvents.planStepPlanSelected(availablePlan.description),
                  },
                  user: getDataLayerInfo.user,
                });

                setPlanSelected(availablePlan._id, availablePlan.type);
              }}
              branding={branding}
            />
          ))}
        </div>
        <div className={styles.plan_steps_card_footer_col}>
          <Text
            text={plansStepTexts.warningUF(valueUF, today)}
            customClass={styles.infor_warning_uf}
          />
        </div>
      </div>
    </div>
  );
}

export default PlansStep;
